import './hud.css';
import { pairs, t } from '../../i18n/i18n';
import type { ListKey } from '../../i18n/i18n';
import type { Bout } from '../../rules/bout';
import type { Fighter } from '../../rules/fighter';
import { Signal } from '../../rules/signal';
import type { BoutResult, Side } from '../../rules/types';
import { isTyping } from '../../input/typing';
import { formatClock } from './clock';
import { el, replay } from './dom';

/** One fighter's bars: health, with a white ghost of the damage just taken draining after it, and stamina under it. */
class FighterBars {
  private readonly healthBar: HTMLElement;
  private readonly health: HTMLElement;
  private readonly ghost: HTMLElement;
  private readonly staminaBar: HTMLElement;
  private readonly stamina: HTMLElement;
  private shown = 1;
  private ghostShown = 1;
  private ghostWait = 0;

  constructor(parent: HTMLElement, side: Side, name: string) {
    const root = el('div', `fighter ${side}`, parent);
    el('div', 'fighter-name', root, name);
    this.healthBar = el('div', 'bar', root);
    this.ghost = el('div', 'ghost', this.healthBar);
    this.health = el('div', 'fill', this.healthBar);
    this.staminaBar = el('div', 'bar stamina', root);
    this.stamina = el('div', 'fill', this.staminaBar);
  }

  show(fighter: Fighter, seconds: number): void {
    const health = fighter.health.fraction;
    if (health < this.shown) this.ghostWait = 0.45;
    this.shown = health;
    if (this.ghostWait > 0) this.ghostWait -= seconds;
    else this.ghostShown = Math.max(health, this.ghostShown - seconds * 0.5);
    this.ghostShown = Math.max(this.ghostShown, health);
    this.health.style.transform = `scaleX(${health})`;
    this.ghost.style.transform = `scaleX(${this.ghostShown})`;
    this.healthBar.classList.toggle('low', health > 0 && health < 0.25);
    this.stamina.style.transform = `scaleX(${fighter.staminaFraction})`;
    this.staminaBar.classList.toggle('empty', fighter.staminaFraction < 0.2);
  }

  flashTired(): void {
    replay(this.staminaBar, 'flash');
  }
}

/**
 * Everything drawn over the ring in HTML: both fighters' health and stamina, the round and its clock, the super meter
 * and its button, short hints, the big words of the bout, its result, and the list of controls. It only shows what the
 * rules say; the buttons speak through its signals.
 */
export class Hud {
  readonly superPressed = new Signal<void>();
  readonly againPressed = new Signal<void>();
  readonly menuPressed = new Signal<void>();
  private readonly player: FighterBars;
  private readonly opponent: FighterBars;
  private readonly roundLabel: HTMLElement;
  private readonly clock: HTMLElement;
  private readonly segments: HTMLElement[] = [];
  private readonly superButton: HTMLButtonElement;
  private readonly hint: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly result: HTMLElement;
  private readonly resultTitle: HTMLElement;
  private readonly resultHow: HTMLElement;
  private readonly resultStats: HTMLElement;
  private readonly resultCoins: HTMLElement;
  private readonly superBox: HTMLElement;
  private readonly meter: HTMLElement;
  private readonly controls: HTMLElement;
  private readonly hurt: HTMLElement;
  private readonly speedLines: HTMLElement;
  private readonly callout: HTMLElement;
  private hintLeft = 0;

  constructor(private readonly root: HTMLElement, superHits: number, private readonly opponentName: string) {
    this.hurt = el('div', 'hurt', root);
    this.speedLines = el('div', 'speedlines', root);
    this.speedLines.style.backgroundImage = `url(${speedLinesImage()})`;
    this.callout = el('div', 'callout', root);
    el('div', 'letterbox top', root);
    el('div', 'letterbox bottom', root);
    const top = el('div', 'hud-top', root);
    this.player = new FighterBars(top, 'player', t('you'));
    const round = el('div', 'round-box', top);
    this.roundLabel = el('div', 'round-label', round);
    this.clock = el('div', 'round-clock', round);
    this.opponent = new FighterBars(top, 'opponent', opponentName);

    const bottom = el('div', 'hud-bottom', root);
    this.superBox = bottom;
    this.meter = el('div', 'super-meter', bottom);
    this.setSuperHits(superHits);
    this.superButton = el('button', 'super-button', bottom, t('super'));
    el('span', 'key', this.superButton, t('super-key'));
    this.superButton.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.superPressed.emit();
    });

    this.hint = el('div', 'hint', root);
    this.banner = el('div', 'banner', root);

    this.result = el('div', 'result', root);
    const card = el('div', 'result-card', this.result);
    this.resultTitle = el('div', 'result-title', card);
    this.resultHow = el('div', 'result-how', card);
    this.resultStats = el('div', 'result-stats', card);
    this.resultCoins = el('div', 'result-coins', card);
    const buttons = el('div', 'result-buttons', card);
    el('button', 'big-button', buttons, t('again')).addEventListener('click', () => this.againPressed.emit());
    el('button', 'glass-button', buttons, t('menu')).addEventListener('click', () => this.menuPressed.emit());

    this.controls = el('div', 'controls', root);
    this.controls.hidden = true;
    this.controlsList('controls-keys', 'controls-key-list');
    this.controlsList('controls-mouse', 'controls-mouse-list');
    el('button', 'controls-button', root, t('controls')).addEventListener('click', () => this.toggleControls());

    window.addEventListener('keydown', (event) => {
      if (isTyping(event)) return;
      if (event.code === 'Tab') {
        event.preventDefault();
        this.toggleControls();
      }
      if (event.code === 'Enter' && this.result.classList.contains('shown')) this.againPressed.emit();
    });
  }

  /** Every frame: the bars, the round and its clock, the super meter, the hint running out. */
  show(bout: Bout, seconds: number): void {
    this.player.show(bout.player, seconds);
    this.opponent.show(bout.opponent, seconds);
    const resting = bout.phase === 'rest';
    this.setText(this.roundLabel, resting ? t('rest') : t('round-of', bout.round, bout.rules.rounds));
    this.setText(this.clock, formatClock(bout.secondsLeft));
    this.clock.classList.toggle('hurry', !resting && bout.phase === 'round' && bout.secondsLeft <= 10);

    const player = bout.player;
    const lit = Math.round(player.superCharge * this.segments.length);
    this.segments.forEach((segment, i) => segment.classList.toggle('on', i < lit));
    this.superButton.classList.toggle('ready', player.superCharge >= 1 && bout.phase === 'round' && player.state !== 'knockedOut');

    if (this.hintLeft > 0 && (this.hintLeft -= seconds) <= 0) this.hint.classList.remove('shown');
  }

  /** Red at the edges of the view, 0..1: a hit just taken, or little health left. */
  setHurt(level: number): void {
    const opacity = level.toFixed(2);
    if (this.hurt.style.opacity !== opacity) this.hurt.style.opacity = opacity;
  }

  /** A big word across the ring for a moment. */
  announce(text: string, kind: '' | 'is-fight' | 'is-ko' = ''): void {
    this.banner.textContent = text;
    this.banner.className = `banner ${kind}`;
    replay(this.banner, 'play');
  }

  /** The punch is not bought yet. */
  locked(): void {
    this.showHint(t('locked'));
  }

  /** The player's punch cut the opponent's off before it landed. */
  interrupted(): void {
    this.shout(t('interrupted'));
  }

  /** Clean hits one after another. */
  combo(hits: number): void {
    this.shout(t('combo', hits));
  }

  /** His punch to the head went past a dodge. */
  slipped(): void {
    this.shout(t('slipped'), 'is-counter');
  }

  /** Ducked, and caught by a body blow all the same: blocking is what stops those. */
  bodyNotDodged(): void {
    this.showHint(t('body-not-dodged'));
  }

  /** A clean hit straight after blocking or slipping his. */
  counter(): void {
    this.shout(t('counter'), 'is-counter');
  }

  /** Anime speed lines rushing in from the edges, for a moment. */
  rush(): void {
    replay(this.speedLines, 'play');
  }

  /** The punch needs more stamina than he has. */
  tired(): void {
    this.player.flashTired();
    this.showHint(t('tired'));
  }

  /** Black bars top and bottom, and the fight's bars and meters away, while the camera films a moment of its own. */
  setCinematic(on: boolean): void {
    const value = on ? 'on' : 'off';
    if (this.root.dataset.cinematic !== value) this.root.dataset.cinematic = value;
  }

  /** The menu and the shop hide the fight's bars and meters. */
  setMode(mode: 'menu' | 'shop' | 'fight'): void {
    this.root.dataset.mode = mode;
  }

  /** One segment of the super meter for each clean hit it takes. */
  setSuperHits(hits: number): void {
    this.meter.replaceChildren();
    this.segments.length = 0;
    for (let i = 0; i < hits; i++) this.segments.push(el('span', '', this.meter));
  }

  /** The super meter only shows once the super punch is bought. */
  setSuperOwned(owned: boolean): void {
    this.superBox.hidden = !owned;
  }

  /** The bout's result, and the coins it paid (or, below zero, took). */
  showResult(result: BoutResult, coins: number): void {
    const title = result.winner === 'player' ? 'victory' : result.winner === 'opponent' ? 'defeat' : 'draw';
    this.resultTitle.textContent = t(title);
    this.resultTitle.className = `result-title ${title}`;
    this.resultHow.textContent = result.byKnockout ? t('by-knockout', result.round) : t('on-points');
    this.resultStats.replaceChildren();
    el('div', '', this.resultStats);
    el('div', 'head player', this.resultStats, t('you'));
    el('div', 'head opponent', this.resultStats, this.opponentName);
    const rows = [
      [t('stat-thrown'), result.player.thrown, result.opponent.thrown],
      [t('stat-landed'), result.player.landed, result.opponent.landed],
      [t('stat-blocked'), result.player.blocked, result.opponent.blocked],
      [t('stat-dodged'), result.player.dodged, result.opponent.dodged],
      [t('stat-damage'), result.player.damageDealt, result.opponent.damageDealt],
    ] as const;
    for (const [label, mine, his] of rows) {
      el('div', '', this.resultStats, label);
      el('div', 'num', this.resultStats, String(mine));
      el('div', 'num', this.resultStats, String(his));
    }
    this.resultCoins.textContent = coins > 0 ? t('coins-won', coins) : coins < 0 ? t('coins-lost', -coins) : t('coins-none');
    this.resultCoins.classList.toggle('lost', coins < 0);
    this.result.classList.add('shown');
  }

  hideResult(): void {
    this.result.classList.remove('shown');
  }

  toggleControls(): void {
    this.controls.hidden = !this.controls.hidden;
  }

  private shout(text: string, kind = ''): void {
    this.callout.textContent = text;
    this.callout.className = `callout ${kind}`;
    replay(this.callout, 'play');
  }

  private showHint(text: string, mood: '' | 'good' = ''): void {
    this.hint.textContent = text;
    this.hint.className = `hint ${mood} shown`;
    this.hintLeft = 1.5;
  }

  private controlsList(title: 'controls-keys' | 'controls-mouse', list: ListKey): void {
    el('h3', '', this.controls, t(title));
    const items = el('dl', '', this.controls);
    for (const [what, does] of pairs(list)) {
      el('dt', '', items, what);
      el('dd', '', items, does);
    }
  }

  // Only touches the page when the text changes: most frames it does not.
  private setText(node: HTMLElement, text: string): void {
    if (node.textContent !== text) node.textContent = text;
  }
}

// Speed lines drawn once: thin rays of random width and length from round the middle out past the edges.
function speedLinesImage(): string {
  const size = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const g = canvas.getContext('2d')!;
  g.translate(size / 2, size / 2);
  for (let i = 0; i < 150; i++) {
    const a = Math.random() * Math.PI * 2;
    const half = 0.003 + Math.random() * 0.011;
    const inner = size * (0.24 + Math.random() * 0.16);
    const outer = size * 0.75;
    g.beginPath();
    g.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
    g.lineTo(Math.cos(a - half) * outer, Math.sin(a - half) * outer);
    g.lineTo(Math.cos(a + half) * outer, Math.sin(a + half) * outer);
    g.closePath();
    g.fillStyle = `rgba(255, 255, 255, ${0.3 + Math.random() * 0.55})`;
    g.fill();
  }
  return canvas.toDataURL();
}
